import {
  apiItemSchema,
  isKnownItemType,
  itemContentSchemas,
  type ApiItem,
} from '@sciolingo/api-client'
import { api } from '../api'
import { META_KEYS, db, metaSet } from '../db/db'
import type { CachedItem, PreparationRecord } from '../db/types'

/**
 * Per-session prefetch, tier T1 (§5.3, §5.4).
 *
 * The goal is narrow and worth restating: *losing connectivity must never
 * prevent answering the next item.* Everything else may require connectivity.
 *
 * Runs on screen entry rather than on the Start tap (§5.1), which moves the
 * network moment earlier and makes it invisible. Waiting for the tap would turn
 * that tap into a single point of failure on unstable school Wi-Fi — the exact
 * situation this design exists to survive.
 */

export interface PrefetchOutcome {
  preparation: PreparationRecord
  cachedCount: number
  skipped: { itemId: string; type: string; reason: 'unknown-type' | 'invalid-content' }[]
}

export async function prefetchForDescriptor(descriptorId: string): Promise<PrefetchOutcome> {
  const payload = await api.prepareSession(descriptorId)

  const cachedAt = Date.now()
  const skipped: PrefetchOutcome['skipped'] = []
  const toStore: CachedItem[] = []

  for (const raw of payload.items) {
    const parsed = apiItemSchema.safeParse(raw)
    if (!parsed.success) {
      skipped.push({ itemId: String((raw as ApiItem | undefined)?.id ?? '?'), type: '?', reason: 'invalid-content' })
      continue
    }
    const item = parsed.data

    // §7.4: unknown types are skipped with a logged warning rather than
    // crashing the session, which is what keeps server-driven content
    // additions safe for clients already in students' pockets.
    if (!isKnownItemType(item.type)) {
      console.warn(`[prefetch] skipping item ${item.id}: unknown type "${item.type}" (§7.4)`)
      skipped.push({ itemId: item.id, type: item.type, reason: 'unknown-type' })
      continue
    }

    // Validated at cache time; the renderer validates again at render time.
    const content = itemContentSchemas[item.type].safeParse(item.content)
    if (!content.success) {
      console.warn(`[prefetch] skipping item ${item.id}: content failed validation`, content.error.issues)
      skipped.push({ itemId: item.id, type: item.type, reason: 'invalid-content' })
      continue
    }

    toStore.push({
      id: item.id,
      version: item.version,
      type: item.type,
      groupId: item.groupId ?? null,
      content: item.content,
      correctAnswer: item.correctAnswer ?? null,
      explanation: item.explanation ?? null,
      assetIds: item.assetIds,
      servedAt: item.servedAt,
      cachedAt,
    })
  }

  // Items are disposable — they are re-fetchable (§6.1) — so a bulkPut that
  // overwrites an older cached copy is exactly the desired behaviour.
  await db.items.bulkPut(toStore)

  const preparation: PreparationRecord = {
    descriptorId,
    preparationId: payload.preparationId,
    itemIds: toStore.map((i) => i.id),
    skippedItemIds: skipped.map((s) => s.itemId),
    servedAt: payload.servedAt,
    cachedAt,
  }
  await metaSet(META_KEYS.preparation(descriptorId), preparation)

  return { preparation, cachedCount: toStore.length, skipped }
}

export interface PrefetchRetryOptions {
  /** Aborting stops the retries and rejects with the signal's reason. */
  signal?: AbortSignal
  /** Called when an attempt fails and another is scheduled, with the zero-based index of the next one. */
  onRetry?: (attempt: number) => void
  maxAttempts?: number
  /** The wait before the retry that follows `attempt`. Defaults to `backoffDelayMs`. */
  backoff?: (attempt: number) => number
}

/**
 * `prefetchForDescriptor`, retried with backoff. On a flaky network, retries
 * happen without user action. The caller shows an explicit failure only after
 * the last attempt, once retrying on its own has stopped being plausible, so the
 * student is told rather than left watching a spinner.
 */
export async function prefetchWithRetry(
  descriptorId: string,
  { signal, onRetry, maxAttempts = 5, backoff = backoffDelayMs }: PrefetchRetryOptions = {},
): Promise<PrefetchOutcome> {
  for (let attempt = 0; ; attempt += 1) {
    signal?.throwIfAborted()
    try {
      return await prefetchForDescriptor(descriptorId)
    } catch (error) {
      if (attempt + 1 >= maxAttempts) throw error
      const delay = backoff(attempt)
      console.warn(`[prefetch] attempt ${attempt + 1} failed, retrying in ${delay} ms`, error)
      onRetry?.(attempt + 1)
      await abortableDelay(delay, signal)
    }
  }
}

function abortableDelay(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason as Error)
      return
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    function onAbort(): void {
      clearTimeout(timer)
      reject(signal?.reason as Error)
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/** Exponential backoff with jitter — on a flaky network, prefetch retries without user action. */
export function backoffDelayMs(attempt: number, baseMs = 500, capMs = 8_000): number {
  const exponential = Math.min(capMs, baseMs * 2 ** attempt)
  return Math.round(exponential / 2 + Math.random() * (exponential / 2))
}
